import { HttpClient, HttpContext } from '@angular/common/http';
import { DOCUMENT, Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { firstValueFrom } from 'rxjs';

import { HIDE_ERROR_TOAST } from '../../core/constant/http-context.constant';
import { IAppState } from '../../store/interface/app.state.interface';
import { IUser } from '../../core/interfaces/user.interface';
import { IResponse } from '../../shared/interfaces/response.interface';
import { IHandOverAddress, ISignIn, IToken } from '../interfaces/auth.interface';
import { TokenStoreService } from '../../core/services/token-store.service';
import { environment } from '../../../environments/environment';
import * as UserActions from '../../store/user/user.actions';

@Injectable()
export class AuthService {
    private readonly document = inject(DOCUMENT);
    private readonly http = inject(HttpClient);
    private readonly store = inject(Store<IAppState>);
    private readonly tokenStore = inject(TokenStoreService);
    private readonly signInUrl = '/auth/sign-in';
    private readonly codeUrl = '/auth/sign-in/code';
    private readonly meUrl = '/auth/me';
    private readonly signOutUrl = '/auth/sign-out';
    private readonly handOverUrl = '/auth/hand-over';
    private readonly googleUrl = '/auth/google/login';

    async signIn(email: string, password: string): Promise<string> {
        return firstValueFrom(
            this.http.post<IResponse<ISignIn>>(this.signInUrl, { email, password }),
        ).then((res) => res.data.otpId);
    }

    async resendCode(otpId: string): Promise<void> {
        await firstValueFrom(this.http.post(`${this.codeUrl}/resend`, { otpId }));
    }

    async verifyCode(otpId: string, code: string): Promise<void> {
        const { token, ...user } = await firstValueFrom(
            this.http.post<IResponse<IToken>>(this.codeUrl, { otpId, code }),
        ).then((res) => res.data);

        this.tokenStore.save(token);
        this.store.dispatch(UserActions.setLoggedInUser({ user }));
    }

    async fetchLoggedInUser(hideErrorToast = false): Promise<void> {
        const context = new HttpContext().set(HIDE_ERROR_TOAST, hideErrorToast);
        return firstValueFrom(this.http.get<IResponse<IUser>>(this.meUrl, { context })).then(
            (res) => {
                this.store.dispatch(UserActions.setLoggedInUser({ user: res.data }));
            },
        );
    }

    async logout(): Promise<void> {
        await firstValueFrom(this.http.post(this.signOutUrl, {}));
    }

    async exitToAccountApp(): Promise<void> {
        const { redirectUrl } = await firstValueFrom(
            this.http.post<IResponse<IHandOverAddress>>(this.handOverUrl, {}),
        ).then((res) => res.data);

        this.tokenStore.clear();
        this.document.location.assign(redirectUrl);
    }

    // A page the browser opens, not an API call, so it needs the API's full address.
    getGoogleSignInUrl(): string {
        return `${environment.apiHost}${environment.basePath}${this.googleUrl}`;
    }
}
